import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import type { Dayjs } from 'dayjs';
import {
  $accountCardListStore,
  $accountCardReportStore,
  $cardListStore,
  $initialCurrenciesAvailableStore,
  $profileFeatureStore,
  $reportStore,
  $transactionStore,
} from '@lib/store';
import { useListState } from '@shared/hooks';
import { cn, FormatUtils, LocalStorageUtils, PaletteUtils } from '@shared/utils';
import { ReportSecondaryTabs } from '../../models';
import {
  type AccountResponse,
  AccountType,
  type CardResponse,
  type GenerateTransactionReportRequest,
  ReportExportFileType,
  TransactionReportColumnsEnum,
} from '@shared/api';
import { AccountTable } from './accountTable';
import { TransactionTable } from '../creditTab';
import { ColumnsModal } from '../columnsModal';
import { GeneratedFileModal } from '../generatedFileModal';
import { Button, Icon, Modal, Radio, SearchInput, Select, SelectDateRange, Spinner, Tabs } from '@shared/widgets';
import DownloadIcon from '@assets/icons/download.svg';
import PlusIcon from '@assets/icons/plus.svg';

enum ColumnsEnum {
  STATUS = 'Status',
  CARD = 'Card',
  DATE = 'Date',
  DESCRIPTION = 'Description',
  AMOUNT = 'Amount',
  FEE = 'Fee',
  TOTALSPENT = 'TotalSpent',
  CATEGORY = 'Category',
  RECEIPT = 'Receipt',
  NOTE = 'Note',
}

const COLUMNS = [
  ColumnsEnum.STATUS,
  ColumnsEnum.CARD,
  ColumnsEnum.DATE,
  ColumnsEnum.DESCRIPTION,
  ColumnsEnum.AMOUNT,
  ColumnsEnum.FEE,
  ColumnsEnum.TOTALSPENT,
  ColumnsEnum.CATEGORY,
  ColumnsEnum.RECEIPT,
  ColumnsEnum.NOTE,
];

const mapColumnToEnum = (column: string): TransactionReportColumnsEnum | undefined => {
  const columns: { [key: string]: TransactionReportColumnsEnum } = {
    [ColumnsEnum.STATUS]: TransactionReportColumnsEnum.STATUS,
    [ColumnsEnum.CARD]: TransactionReportColumnsEnum.CARD,
    [ColumnsEnum.DATE]: TransactionReportColumnsEnum.DATE,
    [ColumnsEnum.DESCRIPTION]: TransactionReportColumnsEnum.DESCRIPTION,
    [ColumnsEnum.AMOUNT]: TransactionReportColumnsEnum.AMOUNT,
    [ColumnsEnum.FEE]: TransactionReportColumnsEnum.FEE,
    [ColumnsEnum.TOTALSPENT]: TransactionReportColumnsEnum.TOTALSPENT,
    [ColumnsEnum.CATEGORY]: TransactionReportColumnsEnum.CATEGORY,
    [ColumnsEnum.RECEIPT]: TransactionReportColumnsEnum.RECEIPT,
    [ColumnsEnum.NOTE]: TransactionReportColumnsEnum.NOTE,
  };

  return columns[column];
};

export const ReportCreditTab = () => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const transactionStore = useUnit($transactionStore.store);
  const cardStore = useUnit($cardListStore.store);
  const accountsStore = useUnit($accountCardListStore.store);
  const accountReportStore = useUnit($accountCardReportStore.store);
  const reportStore = useUnit($reportStore.store);

  const [tab, setTab] = useState<ReportSecondaryTabs>(ReportSecondaryTabs.Transactions);
  const [searchText, setSearchText] = useState('');
  const [showGeneratedModal, setShowGeneratedModal] = useState<boolean>(false);
  const isTransactionTab = tab === ReportSecondaryTabs.Transactions;

  const columns = LocalStorageUtils.getCardTransactionColumns();
  const [isOpenFieldsModal, setIsOpenFieldsModal] = useState<boolean>(false);
  const [transactionColumns, setTransactionColumns] = useState<string[]>(columns || COLUMNS);
  const updateColumns = (activeColumns: string[]) => {
    setTransactionColumns(activeColumns);
    LocalStorageUtils.setCardTransactionColumns(activeColumns);
  };

  const changeTabHandler = (tab: ReportSecondaryTabs) => {
    setTab(tab);
    setCurrency(undefined);
  };

  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [account, setAccount] = useState<AccountResponse | undefined>(undefined);
  const [card, setCard] = useState<CardResponse | undefined>(undefined);
  const [dateRange, setDateRange] = useState<{ dateFrom?: Dayjs; dateTo?: Dayjs } | undefined>(undefined);
  const [isOpenExportModal, setIsOpenExportModal] = useState(false);

  const reportsCardsTransactionsState = useListState({ list: 'reportsCardsTransactions' });
  const reportsCardsAccountsState = useListState({ list: 'reportsCardsAccounts' });

  const { t } = useTranslation('reports');

  const updateAccounts = () => {
    $accountCardReportStore.events.getCardAccountBalanceReport({
      Currency: currency,
      ReturnTotalCount: true,
      Count: reportsCardsAccountsState.count,
      FromIndex: reportsCardsAccountsState.fromIndex,
    });
  };

  const updateTransactions = () => {
    $transactionStore.events.getTransactions({
      Currency: currency,
      TextSearch: searchText,
      AccountIds: account?.id ? [account?.id] : undefined,
      CardId: card?.cardId,
      DateFrom: dateRange?.dateFrom?.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      DateTo: dateRange?.dateTo?.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
      ReturnTotalCount: true,
      Count: reportsCardsTransactionsState.count,
      FromIndex: reportsCardsTransactionsState.fromIndex,
    });
  };

  const onExport = (item: ReportExportFileType) => {
    setIsOpenExportModal(false);
    if (isTransactionTab) {
      const columnsVisible = transactionColumns.map(mapColumnToEnum).filter((col) => !!col);

      const request: GenerateTransactionReportRequest = {
        Currency: currency,
        AccountId: account?.id,
        CardId: card?.cardId,
        ReportType: item,
        FromDate: dateRange?.dateFrom?.startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        ToDate: dateRange?.dateTo?.endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
        ColumnsVisible: columnsVisible,
      };

      $reportStore.events.generateTransactionReport(request);
    } else {
      $reportStore.events.generateCardAccountBalanceReport({
        ReportType: item,
        Currency: currency,
      });
    }
  };

  useEffect(() => {
    if (!reportStore.loading && reportStore.data) setShowGeneratedModal(true);
  }, [reportStore.data, reportStore.loading]);

  useEffect(() => {
    if (tab != ReportSecondaryTabs.AccountsBalance) return;
    if (!profileFeatureStore.data?.reports.viewAccountBalanceReport) return;
    updateAccounts();
  }, [tab, currency, reportsCardsAccountsState]);

  useEffect(() => {
    if (tab != ReportSecondaryTabs.Transactions) return;
    updateTransactions();
  }, [tab, currency, searchText, dateRange, card, account, reportsCardsTransactionsState]);

  useEffect(() => {
    $accountCardListStore.events.getAccounts({ AccountType: AccountType.Card });
    if (profileFeatureStore.data?.reports?.viewAccountBalanceReport) $accountCardListStore.events.getAccounts();
    $cardListStore.events.getCards({ Count: 100 });

    return () => {
      $accountCardReportStore.events.clear();
      $accountCardListStore.events.clear();
      $transactionStore.events.clear();
      $cardListStore.events.clear();
    };
  }, []);

  const tabOptions = useMemo(
    () =>
      Object.entries(ReportSecondaryTabs)
        .map(([, value]) => ({ label: t(value), value }))
        .filter(
          (tab) =>
            tab.value === ReportSecondaryTabs.Transactions ||
            (profileFeatureStore.data?.reports?.viewAccountBalanceReport && tab.value === ReportSecondaryTabs.AccountsBalance),
        ),
    [t, profileFeatureStore.data?.reports?.viewAccountBalanceReport],
  );
  const exportOptions = useMemo(() => Object.entries(ReportExportFileType).map(([value, label]) => ({ text: t(label), value })), [t]);
  return (
    <div className='flex h-full w-full flex-col'>
      <div className='my-4 flex items-center gap-2'>
        {isTransactionTab && <SearchInput value={searchText} onChange={setSearchText} />}
        <div className='ml-auto flex gap-2'>
          {isTransactionTab && (
            <Button color='primary' onClick={() => setIsOpenFieldsModal(true)}>
              <Icon icon={<PlusIcon />} color='inherit' />
              {t('buttons.addFilter')}
            </Button>
          )}
          <Button
            color='primary'
            disabled={isTransactionTab ? !transactionStore.data.data.length : !accountReportStore.data.data.length}
            onClick={() => setIsOpenExportModal(true)}
          >
            <Icon icon={<DownloadIcon />} color='inherit' />
            {t('buttons.export')}
          </Button>
        </div>
      </div>
      <div className={cn('flex justify-between', PaletteUtils.isVcfcPalette && 'max-md:flex-col-reverse')}>
        <Tabs
          className='w-full'
          listClassName='h-full gap-0'
          labelClassName={cn('p-4', 'hover:bg-white dark:hover:bg-soft-black')}
          activeLabelClassName='bg-white dark:bg-soft-black'
          options={tabOptions}
          hideIndicator
          value={tab}
          onChange={changeTabHandler}
        />
        <div className={cn('my-auto mb-6 flex', PaletteUtils.isVcfcPalette && 'max-md:flex-col max-md:gap-2')}>
          <Select
            className='min-w-[160px]'
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
            value={currency}
            onChange={setCurrency}
          />
          {isTransactionTab && (
            <>
              <Select
                className={cn('ml-6', PaletteUtils.isVcfcPalette && 'max-md:ml-0')}
                items={accountsStore.data.map((x) => ({ label: FormatUtils.toSliceNumber(x.accountNumber) || '', value: x }))}
                nullValue={{ isEnable: true, text: t('placeholders.allAccounts', { ns: 'common' }) }}
                value={account}
                onChange={setAccount}
              />
              {cardStore.data?.data && (
                <Select
                  className={cn('ml-6', PaletteUtils.isVcfcPalette && 'max-md:ml-0')}
                  items={cardStore.data.data.map((x) => ({ label: FormatUtils.toSliceNumber(x.cardNumber) || '', value: x }))}
                  nullValue={{ isEnable: true, text: t('placeholders.allCards', { ns: 'common' }) }}
                  value={card}
                  onChange={setCard}
                />
              )}
              <SelectDateRange className={cn('ml-6', PaletteUtils.isVcfcPalette && 'max-md:ml-0')} value={dateRange} onChange={setDateRange} />
            </>
          )}
        </div>
      </div>
      <div className={cn('flex w-full', PaletteUtils.isVcfcPalette && 'flex-col rounded-b-xl bg-white p-6 dark:bg-soft-black')}>
        {profileFeatureStore.data?.reports?.viewAccountBalanceReport && tab === ReportSecondaryTabs.AccountsBalance && (
          <AccountTable
            isLoading={accountReportStore.loading}
            error={accountReportStore.error}
            onRefresh={updateAccounts}
            accounts={accountReportStore.data.data}
            totalItemCount={accountReportStore.data.total}
            currentPage={reportsCardsAccountsState.page}
            countItemPage={reportsCardsAccountsState.count}
            onChangePage={reportsCardsAccountsState.onChangePage}
            onChangeCountItemPage={reportsCardsAccountsState.onChangeCount}
          />
        )}
        {!!profileFeatureStore.data?.cardFlows?.length &&
          profileFeatureStore.data?.reports?.viewTransactions &&
          tab === ReportSecondaryTabs.Transactions && (
            <TransactionTable
              isLoading={transactionStore.loading}
              error={transactionStore.error}
              onRefresh={updateTransactions}
              columns={transactionColumns}
              items={transactionStore.data.data}
              totalItemCount={transactionStore.data.total}
              currentPage={reportsCardsTransactionsState.page}
              countItemPage={reportsCardsTransactionsState.count}
              onChangePage={reportsCardsTransactionsState.onChangePage}
              onChangeCountItemPage={reportsCardsTransactionsState.onChangeCount}
              onUpdate={updateTransactions}
            />
          )}
      </div>
      <Modal isOpen={isOpenExportModal} onClose={() => setIsOpenExportModal(false)} title={t('modals.download.title')}>
        <div className='flex flex-col'>
          <Radio orientation='vertical' options={exportOptions} onChange={(val) => onExport(val as ReportExportFileType)} />
        </div>
      </Modal>
      {reportStore.loading && <Spinner position />}
      <ColumnsModal
        show={isOpenFieldsModal}
        onClose={() => setIsOpenFieldsModal(false)}
        onSave={updateColumns}
        columns={COLUMNS}
        activeColumns={transactionColumns}
      />
      <GeneratedFileModal open={showGeneratedModal} onClose={() => setShowGeneratedModal(false)} />
    </div>
  );
};
